import React, { useState, useEffect, useCallback } from 'react'
import styles from './index.module.scss'
import CloseIcon from '../../assets/icon/CloseIcon'
import CheckboxActiveIcon from '../../assets/icon/CheckboxActiveIcon'
import CheckboxIcon from '../../assets/icon/CheckboxIcon'
import WorkspaceMembersService from '../../services/workspace-members.service'
import { useWorkspace } from '../../context/workspace.context'
import { useUser } from 'cor-business'
import { extractList, isPendingWorkspaceMember, sortUsersBySelection } from '../../utils/members'

const PENDING_REGISTRATION_LABEL = 'Очікує завершення реєстрації'
const PENDING_REGISTRATION_MESSAGE = 'Користувач ще не створив акаунт і не може бути доданий до проєкту'

interface ProjectMembersPanelProps {
    isOpen: boolean
    onClose: () => void
    project: { id: number } | null
    // Fired after a member is added/removed so the parent can refresh.
    onMembershipChanged?: () => void
    selectionMembers?: ProjectMembersPanelSelectionMember[]
    selectedMemberIds?: string[]
    onSelectionChange?: (selectedIds: string[]) => void
    compact?: boolean
}

export interface ProjectMembersPanelSelectionMember {
    id: number | string
    userId: string
    name: string
    email?: string
    photo?: string
    pendingRegistration?: boolean
}

interface PanelMember {
    id: number | string // workspace-member id
    userId: string
    name: string
    email: string
    photo?: string
    inProject: boolean
    projectMemberId?: number
    busy?: boolean
    isSelf?: boolean
    pendingRegistration?: boolean
}

const getInitials = (name: string, email = '') => {
    const initials = name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2)
    return initials || email[0]?.toUpperCase() || 'U'
}

export default function ProjectMembersPanel({
    isOpen,
    onClose,
    project,
    onMembershipChanged,
    selectionMembers,
    selectedMemberIds = [],
    onSelectionChange,
    compact = false,
}: ProjectMembersPanelProps) {
    const { workspace } = useWorkspace()
    const { user } = useUser()
    const [members, setMembers] = useState<PanelMember[]>([])
    const [search, setSearch] = useState('')
    const [searchFocused, setSearchFocused] = useState(false)
    const [interactionMessage, setInteractionMessage] = useState<string | null>(null)
    const currentUserId = String(user?.user_id ?? user?.id ?? '')
    const isSelectionMode = Array.isArray(selectionMembers)
    const panelMembers: PanelMember[] = isSelectionMode
        ? selectionMembers.map((member) => ({
            ...member,
            email: member.email || '',
            inProject: selectedMemberIds.includes(String(member.userId)),
        }))
        : members

    const loadMembers = useCallback(async () => {
        if (isSelectionMode || !workspace?.id || !project) return

        try {
            const [membersData, photosData, projectMembersData] = await Promise.all([
                WorkspaceMembersService.getMembers(Number(workspace.id)),
                WorkspaceMembersService.getMemberPhotos(Number(workspace.id)).catch(() => null),
                WorkspaceMembersService.getProjectMembers(Number(project.id)).catch(() => null),
            ])
            const all: any[] = extractList(membersData)
            const photoItems: any[] = extractList(photosData)
            const photoMap = new Map<string, string>()
            photoItems.forEach((p: any) => {
                if (p.user_id && p.photo_data_url) photoMap.set(p.user_id, p.photo_data_url)
            })

            const projectMembers = extractList(projectMembersData)
            const projectMemberByUser = new Map<string, number | undefined>()
            projectMembers.forEach((pm: any) => {
                const pmUserId = pm.user_id ?? pm.userId ?? pm.user?.user_id ?? pm.user?.id
                const pmId = pm.id ?? pm.project_member_id ?? pm.member_id
                if (pmUserId != null) {
                    projectMemberByUser.set(String(pmUserId), pmId)
                }
            })

            setMembers(all.map((m) => {
                const userId = m.user_id || m.user?.user_id || ''
                const firstName = m.first_name || m.user?.first_name || ''
                const lastName = m.last_name || m.user?.last_name || ''
                const name = `${firstName} ${lastName}`.trim() || m.email || userId
                const projectMemberId = projectMemberByUser.get(String(userId))
                const isSelf = currentUserId !== '' && String(userId) === currentUserId

                return {
                    id: m.id,
                    userId,
                    name,
                    email: m.email || m.user?.email || '',
                    photo: photoMap.get(userId) || '',
                    inProject: isSelf || projectMemberByUser.has(String(userId)),
                    projectMemberId,
                    isSelf,
                    pendingRegistration: isPendingWorkspaceMember(m),
                }
            }))
        } catch (e) {
            console.error('Failed to load workspace members:', e)
        }
    }, [workspace?.id, project, currentUserId, isSelectionMode])

    useEffect(() => {
        if (!isOpen) return
        void loadMembers()
    }, [isOpen, loadMembers])

    useEffect(() => {
        if (!isOpen) {
            setInteractionMessage(null)
        }
    }, [isOpen])

    const toggleMember = async (member: PanelMember) => {
        if (member.pendingRegistration) {
            setInteractionMessage(PENDING_REGISTRATION_MESSAGE)
            return
        }

        setInteractionMessage(null)

        if (isSelectionMode) {
            const nextIds = member.inProject
                ? selectedMemberIds.filter((id) => id !== member.userId)
                : [...selectedMemberIds, member.userId]
            onSelectionChange?.(nextIds)
            return
        }

        if (!project || member.busy) return
        if (member.isSelf && member.inProject) return

        setMembers((prev) => prev.map((m) => (m.id === member.id ? { ...m, busy: true } : m)))

        try {
            if (member.inProject) {
                await WorkspaceMembersService.removeProjectMember(Number(project.id), member.userId)
                setMembers((prev) => prev.map((m) => (
                    m.id === member.id ? { ...m, inProject: false, projectMemberId: undefined, busy: false } : m
                )))
            } else {
                const created = await WorkspaceMembersService.addProjectMember({
                    user_id: member.userId,
                    project_id: Number(project.id),
                    role_in_project: 'MEMBER',
                })
                setMembers((prev) => prev.map((m) => (
                    m.id === member.id
                        ? { ...m, inProject: true, projectMemberId: created?.id ?? m.projectMemberId, busy: false }
                        : m
                )))
            }

            onMembershipChanged?.()
        } catch (e) {
            console.error('Failed to toggle project membership:', e)
            setMembers((prev) => prev.map((m) => (m.id === member.id ? { ...m, busy: false } : m)))
        }
    }

    const eligibleMembers = panelMembers.filter((member) => !member.pendingRegistration)
    const allInProject = eligibleMembers.length > 0 && eligibleMembers.every((m) => m.isSelf || m.inProject)

    const toggleAll = async () => {
        if (isSelectionMode) {
            onSelectionChange?.(allInProject ? [] : eligibleMembers.map((member) => member.userId))
            return
        }

        if (!project) return

        const targets = members.filter((m) => !m.isSelf && !m.inProject && !m.pendingRegistration)

        await Promise.all(targets.map((m) => toggleMember(m)))
    }

    const currentMember = isSelectionMode ? undefined : panelMembers.find((m) => m.isSelf)

    const projectMemberIds = panelMembers.filter((member) => member.inProject).map((member) => member.userId)
    const filteredMembers = sortUsersBySelection(
        panelMembers.filter((member) => {
            if (member.isSelf) return false
            if (!search) return true
            const query = search.toLowerCase()
            return member.name.toLowerCase().includes(query) || member.email.toLowerCase().includes(query)
        }),
        projectMemberIds,
    )

    if (!isOpen) return null

    return (
        <>
            <div className={styles.overlay} onClick={onClose} />
            <div className={`${styles.panel} ${compact ? styles.panelCompact : ''}`}>
                <div className={styles.panel__header}>
                    <span className={styles.panel__title}>Участники</span>
                    <span className={styles.panel__close} onClick={onClose}>
                        <CloseIcon />
                    </span>
                </div>

                <div className={styles.panel__allRow} onClick={toggleAll}>
                    <span className={styles.panel__allLabel}>Все</span>
                    <span className={styles.panel__checkbox}>
                        {allInProject ? <CheckboxActiveIcon /> : <CheckboxIcon />}
                    </span>
                </div>

                {currentMember && (
                    <div className={`${styles.panel__row} ${styles.panel__rowActive} ${styles.panel__rowSelf}`}>
                        {currentMember.photo ? (
                            <img className={styles.panel__avatar} src={currentMember.photo} alt={currentMember.name} />
                        ) : (
                            <div className={styles.panel__avatarPlaceholder}>{getInitials(currentMember.name, currentMember.email)}</div>
                        )}
                        <span className={styles.panel__memberInfo}>
                            <span className={styles.panel__name}>{currentMember.name}</span>
                        </span>
                        <span className={styles.panel__checkbox}>
                            <CheckboxActiveIcon />
                        </span>
                    </div>
                )}

                <div className={`${styles.panel__search} ${searchFocused ? styles.panel__searchFocused : ''}`}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" clipRule="evenodd" d="M4.99902 11.2922C4.99902 7.8171 7.81612 5 11.2912 5C14.7662 5 17.5833 7.8171 17.5833 11.2922C17.5833 12.7779 17.0685 14.1431 16.2075 15.2196L16.1635 15.2745L18.8148 17.9259C19.0605 18.1716 19.0605 18.57 18.8148 18.8157C18.5691 19.0614 18.1707 19.0614 17.925 18.8157L15.2736 16.1644L15.2187 16.2084C14.1422 17.0695 12.7769 17.5843 11.2912 17.5843C7.81612 17.5843 4.99902 14.7672 4.99902 11.2922ZM11.2912 6.25843C8.51113 6.25843 6.25746 8.51211 6.25746 11.2922C6.25746 14.0722 8.51113 16.3259 11.2912 16.3259C14.0712 16.3259 16.3249 14.0722 16.3249 11.2922C16.3249 8.51211 14.0712 6.25843 11.2912 6.25843Z" fill="currentColor"/>
                    </svg>
                    <input
                        className={styles.panel__searchInput}
                        placeholder="Имя или фамилия"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onFocus={() => setSearchFocused(true)}
                        onBlur={() => setSearchFocused(false)}
                    />
                </div>

                {interactionMessage && (
                    <div className={styles.panel__notice} role="status">
                        {interactionMessage}
                    </div>
                )}

                <div className={styles.panel__list}>
                    {filteredMembers.length === 0 ? (
                        <div className={styles.panel__empty}>Никого не найдено</div>
                    ) : filteredMembers.map((member) => (
                        <div
                            key={member.id}
                            className={`${styles.panel__row} ${member.inProject ? styles.panel__rowActive : ''} ${member.pendingRegistration ? styles.panel__rowPending : ''}`}
                            title={member.pendingRegistration ? PENDING_REGISTRATION_LABEL : undefined}
                            aria-disabled={member.pendingRegistration || undefined}
                            onClick={() => void toggleMember(member)}
                        >
                            {member.photo ? (
                                <img className={styles.panel__avatar} src={member.photo} alt={member.name} />
                            ) : (
                                <div className={styles.panel__avatarPlaceholder}>{getInitials(member.name, member.email)}</div>
                            )}
                            <span className={styles.panel__memberInfo}>
                                <span className={styles.panel__name}>{member.name}</span>
                                {member.pendingRegistration && (
                                    <span className={styles.panel__pendingLabel}>{PENDING_REGISTRATION_LABEL}</span>
                                )}
                            </span>
                            <span className={styles.panel__checkbox}>
                                {member.inProject ? <CheckboxActiveIcon /> : <CheckboxIcon />}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </>
    )
}
